import { Button, Col, Form, FormInstance, Input, message, Row, Space } from 'antd';
import styles from './SettingsWorkDocsComponent.module.scss';
import Icon from '@ant-design/icons';
import { ReactComponent as EditSvg } from '../../../resources/images/edit.svg';
import { ReactComponent as CheckmarkSvg } from '../../../resources/images/checkmark.svg';
import { ReactComponent as CloseSvg } from '../../../resources/images/close-outline.svg';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import userApi from '../../../api/UserApi';
import CustomContext from '../../../context/CustomContext';
import { CurrentUser } from '../../../model/entities';
import notificationService from '../../../services/NotificationService';
import authService from '../../../services/AuthService';

class SettingsWorkDocsComponent extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = { editable: false };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        const { auth } = this.context;
        const { user } = this.props;

        try {
            const username = user.workDocsUsername;
            const password = auth && auth.workDocsAccessToken ? '******' : '';

            this.setState({ username, password });
            this.formRef.current!.setFieldsValue({
                username: username,
                password: password,
            });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    save = async (values: any) => {
        try {
            this.setState({ loading: 'saving' });
            const workDocsAccessToken = await userApi.updateWorkDocs(values.username, values.password);
            const auth = await authService.createAccessToken(workDocsAccessToken);
            this.context.updateAuth(auth);
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            this.switchEditable(false);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    switchEditable = async (editable: boolean) => {
        const { auth } = this.context;

        const password = auth?.workDocsAccessToken && !editable ? '******' : '';
        this.formRef.current!.setFieldsValue({
            password,
        });

        this.setState({ editable });
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        return this.renderForm();
    };

    renderForm = (): React.ReactElement | undefined => {
        const { editable } = this.state;

        return (
            <>
                <Form ref={this.formRef} onFinish={this.save} colon={false} layout="vertical">
                    <Row gutter={[28, 0]}>
                        <Col span={4} className={styles.title}>
                            <FormattedMessage id="settings.platforms.workdocs" />
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label={<FormattedMessage id="settings.platforms.workdocs.username" />}
                                name="username"
                                rules={[
                                    {
                                        type: 'email',
                                        message: <FormattedMessage id="status.email.invalid" />,
                                    },
                                ]}
                                extra={this.renderConnectionStatus()}
                            >
                                <Input size="large" maxLength={200} disabled={!editable} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label={<FormattedMessage id="settings.platforms.workdocs.password" />}
                                name="password"
                            >
                                <Input.Password size="large" maxLength={200} disabled={!editable} />
                            </Form.Item>
                        </Col>
                        <Col span={2} className={styles.actions}>
                            {this.renderButtons(editable)}
                        </Col>
                    </Row>
                </Form>
            </>
        );
    };

    renderButtons = (editable: boolean): React.ReactElement | undefined => {
        const { loading } = this.state;

        return (
            <Space size={8}>
                <Button
                    htmlType="submit"
                    type="primary"
                    icon={<Icon component={CheckmarkSvg} />}
                    loading={loading === 'saving'}
                    hidden={!editable}
                />
                <Button
                    type="default"
                    icon={<Icon component={CloseSvg} />}
                    onClick={() => this.switchEditable(!editable)}
                    disabled={loading === 'saving'}
                    hidden={!editable}
                />
                <Button
                    htmlType="button"
                    type="primary"
                    ghost
                    icon={<Icon component={EditSvg} />}
                    onClick={() => this.switchEditable(!editable)}
                    hidden={editable}
                />
            </Space>
        );
    };

    renderConnectionStatus = (): React.ReactElement | undefined => {
        const { loading } = this.state;
        if (loading === 'saving') {
            return <FormattedMessage id="settings.platforms.workdocs.connecting" />;
        } else if (this.context.auth?.workDocsAccessToken) {
            return (
                <span className={styles.connected}>
                    <FormattedMessage id="settings.platforms.workdocs.connected" />
                </span>
            );
        } else {
            return (
                <span className={styles.disconnected}>
                    <FormattedMessage id="settings.platforms.workdocs.disconnected" />
                </span>
            );
        }
    };

    render() {
        return this.renderForm();
    }
}
export default injectIntl(SettingsWorkDocsComponent);

interface Props extends WrappedComponentProps {
    user: CurrentUser;
}

interface State {
    username?: string;
    password?: string;
    editable: boolean;
    loading?: 'saving';
}
